import store from "@/store";

/**
 * Limite aplicado quando nem a pessoa nem o tribunal definiram um valor.
 *
 * Espelha `DEFAULT_MAX_SIMULTANEOUS_ATTENDANCES` da API e existe só como rede:
 * o padrão do tribunal vem resolvido em `effectiveMaxSimultaneousAttendances`,
 * e esta constante cobre a janela em que o front novo fala com uma API que
 * ainda não devolve o campo. Não é a fonte do número — se divergir do servidor,
 * quem está certo é o servidor.
 */
export const DEFAULT_MAX_SIMULTANEOUS_ATTENDANCES = 1;

/**
 * Indica se o atendente enxerga a tela unificada de atendimento (Omnichannel).
 *
 * Gate **temporário** do piloto de multiatendimento: enquanto a chave não for
 * virada para todo o tribunal, só recebe a tela nova quem tem multiatendimento
 * **explicitamente configurado** com mais de um atendimento — na pessoa ou no
 * padrão do tribunal. Quem não estiver no piloto continua na fila em tabela.
 *
 * Exige `typeof limit === "number"` de propósito. O limite vem resolvido da API
 * em `effectiveMaxSimultaneousAttendances`, e ali `null` significa "sem teto"
 * herdado do booleano legado `isEnableMultiAttendances` — que hoje está ausente
 * em tenants como o do TJ-PB. Tratar esse `null` como "está no piloto" liberava
 * a tela nova para o tribunal inteiro, que é o oposto do faseamento.
 *
 * Só um número configurado abre a tela; a compatibilidade legada continua
 * valendo para a **capacidade** (quantos atendimentos o servidor permite), que
 * é decisão separada desta.
 *
 * Quando o TJ-PB decidir que todos usam a tela — inclusive quem tem limite 1 —,
 * esta função e seus usos devem ser removidos, deixando `/omnichannel` valer
 * para todo mundo.
 *
 * Ver ADR-008 no vault.
 */
export function hasMultiAttendanceExperience() {
  const limit = store?.getters?.user?.effectiveMaxSimultaneousAttendances;

  return typeof limit === "number" && limit > 1;
}

/**
 * Diz se `to` é uma das telas cuja escolha depende do limite de atendimentos
 * simultâneos: a fila em tabela (com o chat legado) e a tela unificada.
 */
export function isAttendanceExperienceRoute(to) {
  return to.name === "OmniChannel" || to.path.startsWith("/attendance");
}

/**
 * Rota de atendimento correspondente à experiência do usuário, ou `null` quando
 * `to` já é a rota certa.
 *
 * A quantidade de atendimentos simultâneos é editável pelo admin a qualquer
 * momento, inclusive com a sessão do atendente aberta. Sem esta checagem, quem
 * já estava numa das telas continuava nela depois do F5: o piloto entrava na
 * fila em tabela e quem saiu do piloto seguia na tela unificada, cada um preso
 * na tela que o limite atual não sustenta mais.
 *
 * Guarda temporária, com o mesmo prazo de `hasMultiAttendanceExperience` —
 * ver ADR-008.
 */
export function resolveAttendanceExperienceRoute(to) {
  const isMultiAttendance = hasMultiAttendanceExperience();

  if (!isMultiAttendance && to.name === "OmniChannel") {
    return { path: "/attendance" };
  }

  // Cobre a fila e o chat legado. Um atendimento em andamento não se perde: o
  // App reabre o chat dentro da tela unificada pelo `attendanceID` da store.
  if (isMultiAttendance && to.path.startsWith("/attendance")) {
    return { path: "/omnichannel" };
  }

  return null;
}
